import { useCallback, useSyncExternalStore } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'drifti:theme';
const THEMES: ThemePreference[] = ['light', 'dark', 'system'];

/** Lys som default — mørk modus er noe man aktivt velger. */
const DEFAULT_THEME: ThemePreference = 'light';

const listeners = new Set<() => void>();
const emit = () => {
  for (const listener of listeners) listener();
};

const prefersDark = () => globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;

export const readThemePreference = (): ThemePreference => {
  const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
  return THEMES.includes(stored as ThemePreference) ? (stored as ThemePreference) : DEFAULT_THEME;
};

const resolve = (preference: ThemePreference) =>
  preference === 'system' ? (prefersDark() ? 'dark' : 'light') : preference;

/**
 * Skriver temaet til <html data-theme>, som er selektoren hero sine
 * fargetokens henger på. Kalles både ved oppstart og ved bytte.
 */
const apply = (preference: ThemePreference) => {
  globalThis.document?.documentElement.setAttribute('data-theme', resolve(preference));
};

/**
 * Leser lagret tema og legger det på <html>. Kalles fra appens entry før
 * render, så et valgt mørkt tema ikke blinker lyst ved oppstart.
 */
export const initTheme = () => {
  apply(readThemePreference());

  // 'system' skal følge OS-en mens appen kjører.
  globalThis.matchMedia?.('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (readThemePreference() === 'system') apply('system');
  });
};

const setThemePreference = (preference: ThemePreference) => {
  globalThis.localStorage?.setItem(STORAGE_KEY, preference);
  apply(preference);
  emit();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  // Bytte i en annen fane skal slå gjennom her også.
  globalThis.addEventListener?.('storage', listener);
  return () => {
    listeners.delete(listener);
    globalThis.removeEventListener?.('storage', listener);
  };
};

/** Temavalget («light» | «dark» | «system») med setter. Persisteres i localStorage. */
export function useTheme() {
  const theme = useSyncExternalStore(subscribe, readThemePreference, () => DEFAULT_THEME);
  const setTheme = useCallback((preference: ThemePreference) => setThemePreference(preference), []);

  return { theme, setTheme, resolvedTheme: resolve(theme) };
}
